import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Camera, Zap, TrendingUp, MapPin, Phone,
  Sparkles, Instagram, Crown, Users, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_MAIN } from "@/data/content";

const IMG = {
  hero: "https://customer-assets.emergentagent.com/job_transylvanian-bread/artifacts/iaehef1c_New%20Ad%202026%20001.png",
  chefBlackRed: "https://customer-assets.emergentagent.com/job_transylvanian-bread/artifacts/n1t0n17e_New%20Ad%202026%20002.png",
  process: "https://customer-assets.emergentagent.com/job_transylvanian-bread/artifacts/mso1q4cb_New%20Ad%202026%20003.png",
  chefRedLogo: "https://customer-assets.emergentagent.com/job_transylvanian-bread/artifacts/fje36ho6_New%20Ad%202026%20004.png",
  packaging: "https://customer-assets.emergentagent.com/job_transylvanian-bread/artifacts/gndmt5it_New%20Ad%202026%20DRACULAS%20FAVORITE.png",
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 0.72, 0.21, 1] },
  }),
};

// Colors ref
// black: #0B0B0B, dark-red: #8B0000, gold: #C9A45C, white: #F5F5F5

/* ------------- Sub-components ------------- */

function ParticleField() {
  // Subtle flour particles (CSS only)
  const particles = Array.from({ length: 26 });
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {particles.map((_, i) => {
        const size = 2 + Math.random() * 4;
        const left = Math.random() * 100;
        const duration = 14 + Math.random() * 22;
        const delay = -Math.random() * duration;
        const opacity = 0.15 + Math.random() * 0.35;
        return (
          <span
            key={i}
            style={{
              left: `${left}%`,
              width: `${size}px`,
              height: `${size}px`,
              opacity,
              animationDuration: `${duration}s`,
              animationDelay: `${delay}s`,
            }}
            className="absolute -top-4 rounded-full bg-[#C9A45C] particle-fall"
          />
        );
      })}
      <style>{`
        @keyframes particleFall {
          0% { transform: translateY(-20px); opacity: 0; }
          15% { opacity: 1; }
          100% { transform: translateY(120vh); opacity: 0; }
        }
        .particle-fall { animation-name: particleFall; animation-timing-function: linear; animation-iteration-count: infinite; filter: blur(0.3px); }
      `}</style>
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} data-testid="inovatie-hero" className="relative min-h-[100svh] flex items-center overflow-hidden bg-[#0B0B0B]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.hero} alt="Transylvania Croissants" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-[#8B0000]/40 to-black/95" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(0,0,0,0.7)_100%)]" />
      </motion.div>
      <ParticleField />

      <div className="relative z-10 w-full max-w-7xl mx-auto container-px py-24 md:py-36">
        <motion.div initial="hidden" animate="visible" className="max-w-3xl">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#C9A45C]/50 bg-black/50 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-[#C9A45C]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#C9A45C]">Inovație • Ediție Limitată</span>
          </motion.div>

          <motion.h1 variants={fadeUp} custom={1} className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[#F5F5F5]">
            Inovația <span className="italic text-[#C9A45C]">are gust.</span> <br />
            <span className="text-[#8B0000]">DOTAPAN</span> <span className="text-white">redefinește</span> <br />
            <span className="italic text-[#F5F5F5]/95">croissantul.</span>
          </motion.h1>

          <motion.p variants={fadeUp} custom={2} className="mt-7 text-base md:text-lg text-white/85 max-w-2xl leading-relaxed">
            La DOTAPAN, tradiția brutăriei transilvănene întâlnește curajul inovației. Am creat două produse speciale,
            memorabile și extrem de instagramabile, gândite pentru HoReCa, retail premium și zone turistice.
          </motion.p>

          <motion.div variants={fadeUp} custom={3} className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/contact">
              <Button data-testid="inovatie-hero-cta" className="rounded-full h-12 px-7 text-sm font-semibold tracking-wide bg-gradient-to-b from-[#8B0000] to-[#5c0000] text-white border border-[#8B0000] hover:brightness-110 shadow-[0_15px_40px_-10px_rgba(139,0,0,0.7)]">
                Cere ofertă B2B <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-full h-12 px-7 text-sm border-[#C9A45C]/60 text-[#C9A45C] hover:bg-[#C9A45C]/10 hover:border-[#C9A45C] bg-black/20 backdrop-blur">
                Contactează-ne
              </Button>
            </Link>
          </motion.div>

          <motion.div variants={fadeUp} custom={4} className="mt-10 flex flex-wrap gap-2.5">
            {["Black Velvet", "Dracula's Favorite", "Made in Transylvania", "Premium Signature"].map((b) => (
              <span key={b} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] rounded-full border border-white/20 text-white/80 bg-white/5 backdrop-blur">
                {b}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Intro() {
  const targets = [
    "Cafenele premium", "Hoteluri", "Resorturi", "Restaurante tematice",
    "Patiserii artizanale", "Zone turistice", "Aeroporturi", "Gift shops",
  ];
  return (
    <section className="relative py-24 md:py-32 bg-[#0B0B0B] text-white overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,0,0,0.25),transparent_60%)]" />
      <div className="relative max-w-6xl mx-auto container-px">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="text-center max-w-3xl mx-auto">
          <motion.p variants={fadeUp} className="text-[11px] uppercase tracking-[0.3em] text-[#C9A45C]">Concept</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="font-display mt-4 text-3xl md:text-5xl leading-tight">
            Două produse. <em className="text-[#8B0000] not-italic">O experiență memorabilă.</em>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="mt-6 text-base md:text-lg text-white/75 leading-relaxed">
            Nu vindem doar croissante. Creăm experiențe. Într-o piață unde produsele clasice se confundă ușor,
            diferențierea devine esențială. De aceea, DOTAPAN a creat două croissante premium care atrag instant
            privirea și rămân în memoria clientului.
          </motion.p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3">
          {targets.map((t) => (
            <div key={t} className="px-4 py-4 rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[#C9A45C]/50 hover:bg-[#C9A45C]/[0.06] transition-all text-center">
              <span className="text-xs md:text-sm uppercase tracking-[0.15em] text-white/80">{t}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ProductBlackVelvet() {
  return (
    <section data-testid="black-velvet-section" className="relative py-24 md:py-32 bg-gradient-to-b from-[#0B0B0B] via-black to-[#0B0B0B] overflow-hidden">
      <div className="max-w-7xl mx-auto container-px grid lg:grid-cols-12 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="lg:col-span-6 order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A45C]/50 bg-black/50">
            <Crown className="h-3.5 w-3.5 text-[#C9A45C]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#C9A45C]">Premium Signature Product</span>
          </div>
          <h2 className="font-display mt-5 text-4xl md:text-6xl text-white leading-tight">
            Black Velvet <br /><em className="text-[#C9A45C] not-italic">Croissant</em>
          </h2>
          <p className="mt-4 font-display italic text-xl md:text-2xl text-white/70">Eleganță întunecată. Gust rafinat.</p>
          <p className="mt-6 text-base md:text-lg text-white/75 leading-relaxed">
            Black Velvet este croissantul care oprește privirea. Cu exterior intens negru, textură fină și straturi
            delicate de aluat cu unt, acest produs transmite lux, mister și exclusivitate. Perfect pentru locațiile
            care vor să ofere ceva diferit.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Aspect premium unic",
              "Extrem de fotogenic",
              "Ideal pentru desert plating",
              "Produs memorabil pentru social media",
            ].map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-sm md:text-base text-white/85">
                <span className="h-6 w-6 rounded-full border border-[#C9A45C]/50 bg-[#C9A45C]/10 flex items-center justify-center text-[#C9A45C] text-xs">✦</span>
                {f}
              </li>
            ))}
          </ul>
          <Link to="/contact" className="inline-block mt-8">
            <Button className="rounded-full h-11 px-6 bg-gradient-to-b from-[#C9A45C] to-[#8a6c34] text-black hover:brightness-110 border border-[#C9A45C]">
              Cere oferta pentru Black Velvet <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black">
            <img src={IMG.chefBlackRed} alt="Black Velvet Croissant" className="w-full h-[560px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />
            <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/70 border border-[#C9A45C]/40 text-[#C9A45C] text-[10px] uppercase tracking-[0.25em] backdrop-blur">
              Ediție limitată
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ProductDracula() {
  return (
    <section data-testid="dracula-section" className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-[#3a0000] via-[#0B0B0B] to-[#0B0B0B]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(139,0,0,0.5),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(139,0,0,0.3),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto container-px grid lg:grid-cols-12 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="lg:col-span-6">
          <div className="relative rounded-3xl overflow-hidden border border-[#8B0000]/40 shadow-[0_40px_80px_-20px_rgba(139,0,0,0.5)]">
            <img src={IMG.chefRedLogo} alt="Dracula's Favorite Croissant" className="w-full h-[600px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/70 border border-[#8B0000]/60 text-[#F5F5F5] text-[10px] uppercase tracking-[0.25em] backdrop-blur flex items-center gap-2">
              <MapPin className="h-3 w-3 text-[#8B0000]" /> Made in Transylvania
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }} className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#8B0000]/60 bg-black/60">
            <Sparkles className="h-3.5 w-3.5 text-[#8B0000]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#F5F5F5]">Tourist Bestseller Potential</span>
          </div>
          <h2 className="font-display mt-5 text-4xl md:text-6xl leading-tight text-[#8B0000] drop-shadow-[0_2px_20px_rgba(139,0,0,0.5)]">
            Dracula&apos;s <br /><span className="text-white">Favorite Croissant</span>
          </h2>
          <p className="mt-4 font-display italic text-xl md:text-2xl text-white/75">Made in Transylvania.</p>
          <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed">
            Inspirat din legenda care a făcut Transilvania celebră în lume, acest croissant rubiniu transformă un produs
            de patiserie într-o experiență turistică. Culoarea intensă, aspectul spectaculos și brandingul memorabil îl
            fac ideal pentru vânzarea în zonele turistice din România.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Produs iconic de suvenir alimentar",
              "Branding memorabil internațional",
              "Ideal pentru turiști",
              "Viral pe social media",
            ].map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-sm md:text-base text-white/90">
                <span className="h-6 w-6 rounded-full border border-[#8B0000]/60 bg-[#8B0000]/20 flex items-center justify-center text-[#8B0000] text-xs">✦</span>
                {f}
              </li>
            ))}
          </ul>
          <Link to="/contact" className="inline-block mt-8">
            <Button className="rounded-full h-11 px-6 bg-gradient-to-b from-[#8B0000] to-[#5c0000] text-white hover:brightness-110 border border-[#8B0000] shadow-[0_15px_30px_-10px_rgba(139,0,0,0.7)]">
              Cere oferta pentru Dracula&apos;s <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function ViralCards() {
  const cards = [
    { icon: Instagram, title: "Instagram Friendly", desc: "Clienții fotografiază produsele spectaculoase." },
    { icon: Zap, title: "Viral Product", desc: "Produse care generează organic content." },
    { icon: TrendingUp, title: "Premium Pricing", desc: "Aspectul unic permite marje superioare." },
    { icon: Users, title: "Tourist Magnet", desc: "Atrage instant turiștii." },
  ];
  return (
    <section className="py-24 md:py-28 bg-[#0B0B0B] text-white">
      <div className="max-w-7xl mx-auto container-px">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center max-w-3xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A45C]">Oportunitate</p>
          <h2 className="font-display mt-4 text-3xl md:text-5xl">De ce aceste produse <em className="text-[#8B0000] not-italic">pot deveni virale?</em></h2>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent hover:border-[#C9A45C]/40 hover:from-[#C9A45C]/[0.08] transition-all group">
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#C9A45C]/20 to-[#8B0000]/20 border border-[#C9A45C]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <c.icon className="h-5 w-5 text-[#C9A45C]" />
              </div>
              <h3 className="mt-5 font-display text-xl text-white">{c.title}</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TouristMap() {
  const spots = [
    { name: "Bran", top: "48%", left: "42%" },
    { name: "Brașov", top: "50%", left: "48%" },
    { name: "Sibiu", top: "56%", left: "38%" },
    { name: "Sighișoara", top: "45%", left: "43%" },
    { name: "Cluj", top: "36%", left: "34%" },
    { name: "București", top: "72%", left: "56%" },
    { name: "Beclean", top: "30%", left: "44%", highlight: true },
  ];
  return (
    <section className="py-24 md:py-32 bg-gradient-to-b from-[#0B0B0B] via-[#150000] to-[#0B0B0B] text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,0,0,0.2),transparent_70%)]" />
      <div className="relative max-w-7xl mx-auto container-px grid lg:grid-cols-12 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-6">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A45C]">România turistică</p>
          <h2 className="font-display mt-4 text-3xl md:text-5xl leading-tight">
            Perfect pentru <em className="text-[#8B0000] not-italic">România turistică.</em>
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed">
            Dracula&apos;s Favorite Croissant poate deveni un produs-emblemă pentru turismul românesc. Exact cum
            Belgia are waffle, Italia are cannoli, iar Franța are croissantul clasic, România poate avea un produs
            iconic cu ADN transilvănean.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-2 text-xs">
            {["Castelul Bran", "Aeroporturi", "Souvenir shops", "Resorturi", "Hoteluri boutique", "Tururi ghidate"].map((x) => (
              <div key={x} className="px-3 py-2 rounded-full border border-white/10 bg-white/[0.03] text-white/80 uppercase tracking-[0.15em]">
                {x}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-6">
          <div className="relative aspect-square max-w-lg mx-auto rounded-3xl border border-[#8B0000]/40 bg-[radial-gradient(ellipse_at_center,rgba(139,0,0,0.15),transparent_70%)] p-6 overflow-hidden">
            {/* Stylized Romania silhouette */}
            <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full p-8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M60,180 C70,140 110,90 160,80 C220,70 280,90 320,100 C350,105 360,140 355,180 C350,220 340,260 320,290 C290,320 250,335 210,335 C170,335 130,320 100,290 C75,265 55,220 60,180 Z"
                fill="rgba(139,0,0,0.08)"
                stroke="rgba(201,164,92,0.5)"
                strokeWidth="2"
                strokeDasharray="6 6"
              />
            </svg>
            {spots.map((s) => (
              <div key={s.name} style={{ top: s.top, left: s.left }} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center group">
                <span className={`h-3 w-3 rounded-full ${s.highlight ? "bg-[#C9A45C] shadow-[0_0_20px_rgba(201,164,92,0.8)]" : "bg-[#8B0000] shadow-[0_0_12px_rgba(139,0,0,0.7)]"} animate-pulse`} />
                <span className={`mt-1.5 text-[10px] uppercase tracking-[0.15em] ${s.highlight ? "text-[#C9A45C] font-bold" : "text-white/80"}`}>
                  {s.name}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PackagingSection() {
  return (
    <section className="py-24 md:py-32 bg-[#0B0B0B] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto container-px grid lg:grid-cols-12 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-6">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A45C]">Ambalaj</p>
          <h2 className="font-display mt-4 text-3xl md:text-5xl leading-tight">
            Packaging <em className="text-[#8B0000] not-italic">care vinde singur.</em>
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed">
            Ambalajul este gândit pentru retail modern și suveniruri premium. Un sigiliu inspirat din blazoanele
            Transilvaniei, tipografie serif dramatică și transparență care lasă produsul să vorbească singur.
          </p>
          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-[#C9A45C] mt-0.5" />
              <div>
                <div className="text-sm font-medium text-white">Sigiliu &laquo;Made in Transylvania&raquo;</div>
                <div className="text-xs text-white/60 mt-0.5">Autenticitate vizuală instantaneu recognoscibilă.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Camera className="h-5 w-5 text-[#C9A45C] mt-0.5" />
              <div>
                <div className="text-sm font-medium text-white">Design premium retail</div>
                <div className="text-xs text-white/60 mt-0.5">Standing out pe raft, în vitrină și în feed.</div>
              </div>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-6">
          <div className="relative rounded-3xl overflow-hidden border border-[#8B0000]/40 bg-black shadow-[0_50px_100px_-20px_rgba(139,0,0,0.5)]">
            <img src={IMG.packaging} alt="Dracula's Favorite Croissant packaging" className="w-full h-[560px] object-cover" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ProcessBanner() {
  return (
    <section className="relative py-16 md:py-20 bg-[#0B0B0B] overflow-hidden">
      <div className="max-w-7xl mx-auto container-px">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border border-[#8B0000]/30">
          <img src={IMG.process} alt="Procesul de coacere: congelat → cuptor → gata de servit" className="w-full h-auto" />
        </motion.div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section data-testid="inovatie-final-cta" className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-b from-[#0B0B0B] via-[#1a0000] to-[#0B0B0B] text-white">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,0,0,0.35),transparent_70%)]" />
      <ParticleField />
      <div className="relative max-w-4xl mx-auto container-px text-center">
        <img src={LOGO_MAIN} alt="DOTAPAN" className="h-16 mx-auto opacity-90" />
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-display mt-8 text-3xl md:text-5xl leading-tight">
          Cauți produse care <em className="text-[#8B0000] not-italic">diferențiază</em> business-ul tău?
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="mt-6 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
          DOTAPAN produce mai mult decât panificație. Produce experiențe care se vând.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/contact">
            <Button data-testid="inovatie-cta-oferta" className="rounded-full h-12 px-8 bg-gradient-to-b from-[#8B0000] to-[#5c0000] text-white border border-[#8B0000] hover:brightness-110 shadow-[0_15px_40px_-10px_rgba(139,0,0,0.7)]">
              Solicită ofertă <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <a href="tel:+40740433894">
            <Button data-testid="inovatie-cta-suna" variant="outline" className="rounded-full h-12 px-8 border-[#C9A45C]/60 text-[#C9A45C] hover:bg-[#C9A45C]/10 bg-black/20">
              <Phone className="h-4 w-4" /> Sună acum
            </Button>
          </a>
        </motion.div>
        <div className="mt-10 text-xs uppercase tracking-[0.3em] text-white/60 space-y-1">
          <div>www.dotapan.ro • +40 740 433 894</div>
          <div className="text-[#C9A45C]">Din inima Transilvaniei, pentru afacerea ta.</div>
        </div>
      </div>
    </section>
  );
}

/* ------------- Page ------------- */

export default function Inovatie() {
  useEffect(() => {
    document.title = "DOTAPAN | Inovație — Transylvania Croissants Premium";
  }, []);
  return (
    <div data-testid="page-inovatie" className="-mt-16 md:-mt-20 bg-[#0B0B0B]">
      <Hero />
      <Intro />
      <ProductBlackVelvet />
      <ProcessBanner />
      <ProductDracula />
      <ViralCards />
      <TouristMap />
      <PackagingSection />
      <FinalCTA />
    </div>
  );
}
